import {
    AssessmentSchedule,
    Issue,
    IssueStatus,
    Location,
    PermissionResourceType,
} from "../../API";
import {
    Box,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TableSortLabelProps,
    Theme,
    Typography,
    useMediaQuery
} from "@mui/material";
import {
    DASHBOARD_END_DATE_KEY,
    DASHBOARD_START_DATE_KEY
} from "../../lib/dashboard/constants/SessionStorageKeys";
import {
    MAX_MONTHS_BACK,
    MAX_MONTHS_FORWARD
} from "../../lib/dashboard/constants/DateConstants";
import {
    Page,
    usePageNavigation
} from "./contexts/PageNavigationContextProvider";
import {
    addDays,
    addMonths,
    differenceInMonths,
    endOfMonth,
    endOfWeek,
    format,
    isAfter,
    isBefore,
    isSameDay,
    isSameMonth,
    isSameWeek,
    isSameYear,
    isToday,
    parseISO,
    startOfDay,
    startOfMonth,
    startOfToday,
    startOfWeek,
    subDays,
    subMonths
} from "date-fns";
import {
    useEffect,
    useMemo,
    useState
} from "react";

import AddIcon from '@mui/icons-material/Add';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { AssessmentStatus } from "../../lib/dashboard/types/AssessmentStatus";
import { CalendarNavigator } from "./CalendarNavigator";
import { CreateIssueButton } from "./issue/CreateIssueButton";
import { CreateLocationButton } from "./CreateLocationButton";
import { EmptyLocationDashboardState } from "./EmptyLocationDashboardState";
import { IssueCountTableCellContent } from "./IssueCountTableCellContent";
import { Legends } from "./Legends";
import { LocationContextMenu } from "./LocationContextMenu";
import { MonthSelect } from "./MonthSelect";
import { ScheduleChecker } from "./ScheduleChecker";
import TodayIcon from '@mui/icons-material/Today';
import { ViewSwitcher } from "./ViewSwitcher";
import { grey } from "@mui/material/colors";
import { useAssessments } from "./contexts/AssessmentsContextProvider";
import { useIssueFilter } from "./contexts/IssueFilterContext";
import { useIssues } from "./contexts/IssuesContextProvider";
import { useLocations } from "./contexts/LocationContextProvider";

const LOCATION_COLUMN_WIDTH_ON_DESKTOP: string = "200px";
const LOCATION_COLUMN_WIDTH_ON_MOBILE: string = "65%";
const DATE_COLUMN_WIDTH_ON_DESKTOP: string = "10%";

const getMonthAbbreviation = (startDate: Date, endDate: Date) => {
    if (isSameMonth(startDate, endDate)) {
        return format(startDate, "MMMM yyyy");
    }
    if (isSameYear(startDate, endDate)) {
        return `${format(startDate, "MMM")}/${format(endDate, "MMM yyyy")}`;
    }
    return `${format(startDate, "MMM yyyy")} / ${format(endDate, "MMM yyyy")}`;
};

export enum VIEW_MODE {
    DAILY = "DAILY",
    WEEKLY = "WEEKLY"
}

export const IssueDashboardTable = () => {
    const { issues } = useIssues();
    const { locations, createLocation, editLocation, deleteLocation } = useLocations();
    const { submitIssue } = useIssues();
    const { assessments, assessmentSchedules, resourceAssessmentScheduleAssociations } = useAssessments();
    const isScreenSmallerThanMd = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
    const isScreenSmallerThanSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
    const viewMode = isScreenSmallerThanSm ? VIEW_MODE.DAILY : VIEW_MODE.WEEKLY;

    const canUseDashboard = useMemo(() => {
        return locations.length > 0;
    }, [locations]);

    const searchParams = useMemo(() => {
        if (typeof window === 'undefined') {
            return new URLSearchParams();
        }
        return new URLSearchParams(window.location.search);
    }, []);
    const [startDate, setStartDate] = useState<Date>(() => {
        const startDateParam = searchParams.get('startDate');
        const startDate = startDateParam ? parseISO(startDateParam) : new Date();
        switch (viewMode) {
            case VIEW_MODE.WEEKLY:
                return startOfWeek(startDate);
            case VIEW_MODE.DAILY:
            default:
                return startDate;
        }
    });
    const [endDate, setEndDate] = useState<Date>(() => {
        const endDateParam = searchParams.get('endDate');
        const endDate = endDateParam ? parseISO(endDateParam) : new Date();
        switch (viewMode) {
            case VIEW_MODE.WEEKLY:
                return endOfWeek(endDate);
            case VIEW_MODE.DAILY:
            default:
                return endDate;
        }
    });

    const [selectedMonth, setSelectedMonth] = useState<string>(getMonthAbbreviation(startDate, endDate));
    const [locationOrderBy, setLocationOrderBy] = useState<TableSortLabelProps["direction"]>("asc");
    const { navigateToPage } = usePageNavigation();
    const { updateFilter } = useIssueFilter();

    const sortedLocations = useMemo(() => {
        return locations.sort((a, b) => {
            if (!a.name || !b.name) {
                return 0;
            }
            if (locationOrderBy === "asc") {
                return a.name.localeCompare(b.name);
            }
            return b.name.localeCompare(a.name);
        });
    }, [locations, locationOrderBy]);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (startDate) {
            const startDateString = format(startDate, "yyyy-MM-dd");
            sessionStorage.setItem(DASHBOARD_START_DATE_KEY, startDateString);
            params.set('startDate', startDateString);
        } else {
            params.delete('startDate');
        }
        if (endDate) {
            const endDateString = format(endDate, "yyyy-MM-dd");
            sessionStorage.setItem(DASHBOARD_END_DATE_KEY, endDateString);
            params.set('endDate', endDateString);
        } else {
            params.delete('endDate');
        }
        const newUrl = `${window.location.pathname}?${params.toString()}`;
        window.history.replaceState({}, '', newUrl);
    }, [startDate, endDate]);

    const navigateToPrev = () => {
        setStartDate(prev => {
            switch (viewMode) {
                case VIEW_MODE.WEEKLY:
                    return startOfWeek(subDays(prev, 7));
                case VIEW_MODE.DAILY:
                default:
                    return subDays(prev, 1);
            }
        });
        setEndDate(prev => {
            switch (viewMode) {
                case VIEW_MODE.WEEKLY:
                    return endOfWeek(subDays(prev, 7));
                case VIEW_MODE.DAILY:
                default:
                    return subDays(prev, 1);
            }
        });
    };

    const navigateToNext = () => {
        setStartDate(prev => {
            switch (viewMode) {
                case VIEW_MODE.WEEKLY:
                    return startOfWeek(addDays(prev, 7));
                case VIEW_MODE.DAILY:
                default:
                    return addDays(prev, 1);
            }
        });
        setEndDate(prev => {
            switch (viewMode) {
                case VIEW_MODE.WEEKLY:
                    return endOfWeek(addDays(prev, 7));
                case VIEW_MODE.DAILY:
                default:
                    return addDays(prev, 1);
            }
        });
    };

    useEffect(() => {
        setSelectedMonth(getMonthAbbreviation(startDate, endDate));
    }, [startDate, endDate]);

    useEffect(() => {
        switch (viewMode) {
            case VIEW_MODE.WEEKLY:
                setStartDate(prev => startOfWeek(prev));
                setEndDate(prev => endOfWeek(prev));
                break;
            case VIEW_MODE.DAILY:
            default:
                setEndDate(startDate);
                break;
        }
    }, [viewMode]);

    const earliestPossibleDate = useMemo<Date>(() => {
        let earliestDate = startOfWeek(subMonths(startOfToday(), MAX_MONTHS_BACK));
        if (issues.length === 0) {
            return earliestDate;
        }
        const firstIssueEpoch = issues.reduce((acc, issue) => {
            if (!issue.clientCreationDate) {
                return acc;
            }
            return Math.min(acc, issue.clientCreationDate);
        }, Number.MAX_SAFE_INTEGER);
        const firstIssueDate = startOfDay(new Date(firstIssueEpoch));
        const startOfWeekFirstIssue = startOfWeek(firstIssueDate);
        return new Date(Math.min(earliestDate.getTime(), startOfWeekFirstIssue.getTime()));
    }, [issues]);

    const latestPossibleDate = useMemo<Date>(() => {
        return endOfWeek(addMonths(startOfToday(), MAX_MONTHS_FORWARD));
    }, []);

    const locationIdToDateToIssuesMap: Map<string, Map<string, Array<Issue>>> = useMemo(() => {
        return issues.reduce((acc: Map<string, Map<string, Array<Issue>>>, issue: Issue) => {
            if (!issue.clientCreationDate) {
                return acc;
            }
            const date = format(startOfDay(new Date(issue.clientCreationDate)), "yyyy-MM-dd");
            const locationId = issue.locationId;
            if (!locationId) {
                return acc;
            }
            if (acc.has(locationId)) {
                if (acc.get(locationId)?.has(date)) {
                    acc.get(locationId)?.get(date)?.push(issue);
                } else {
                    acc.get(locationId)?.set(date, [issue]);
                }
            } else {
                acc.set(locationId, new Map<string, Array<Issue>>([[date, [issue]]]));
            }
            return acc;
        }, new Map<string, Map<string, Array<Issue>>>());
    }, [issues]);

    const assessmentScheduleIdToScheduleCheckerMap: Map<string, ScheduleChecker> = useMemo(() => {
        return assessmentSchedules.reduce((acc: Map<string, ScheduleChecker>, schedule: AssessmentSchedule) => {
            const scheduleChecker = new ScheduleChecker(schedule);
            acc.set(schedule.id, scheduleChecker);
            return acc;
        }, new Map<string, ScheduleChecker>());
    }, [assessmentSchedules]);

    const locationIdToScheduleCheckerMap: Map<string, ScheduleChecker> = useMemo(() => {
        return locations.reduce((acc: Map<string, ScheduleChecker>, location: Location) => {
            const resourceAssessmentScheduleAssociation = resourceAssessmentScheduleAssociations.find(association => association.resourceId === location.id);
            if (!resourceAssessmentScheduleAssociation) {
                return acc;
            }
            const scheduleChecker = assessmentScheduleIdToScheduleCheckerMap.get(resourceAssessmentScheduleAssociation!.assessmentScheduleId ?? "");
            if (scheduleChecker) {
                acc.set(location.id, scheduleChecker);
            }
            return acc;
        }, new Map<string, ScheduleChecker>());
    }, [locations, resourceAssessmentScheduleAssociations, assessmentScheduleIdToScheduleCheckerMap]);

    const dateColumnsToDisplay = useMemo<Array<string>>(() => {
        if (!startDate) return [];
        const daysInView = viewMode === VIEW_MODE.WEEKLY ? 7 : 1;
        return Array(daysInView).fill(0).reduce((acc: Array<Date>, _, index) => {
            return [...acc, format(addDays(startDate, index), "yyyy-MM-dd")];
        }, []);
    }, [startDate, viewMode]);

    const availableMonths = useMemo(() => {
        const monthsDifferenceBetweenEarliestAndLatest = differenceInMonths(endOfMonth(latestPossibleDate), startOfMonth(earliestPossibleDate));
        return Array(monthsDifferenceBetweenEarliestAndLatest).fill(0).reduce((acc: Array<string>, _, index) => {
            return [...acc, format(addMonths(earliestPossibleDate, index), "MMMM yyyy")];
        }, new Array<string>());
    }, [earliestPossibleDate, latestPossibleDate]);

    const disablePreviousWeekButton = useMemo(() => {
        return !canUseDashboard || (isSameWeek(startDate, earliestPossibleDate) || isBefore(startDate, earliestPossibleDate));
    }, [startDate, earliestPossibleDate, canUseDashboard]);

    const disableNextWeekButton = useMemo(() => {
        return !canUseDashboard || (isSameWeek(endDate, latestPossibleDate) || isAfter(endDate, latestPossibleDate));
    }, [endDate, latestPossibleDate, canUseDashboard]);

    const disableTodayButton = useMemo(() => {
        switch (viewMode) {
            case VIEW_MODE.WEEKLY:
                return !canUseDashboard || (isSameWeek(startDate, startOfToday()) || isBefore(startDate, startOfToday()));
            case VIEW_MODE.DAILY:
            default:
                return !canUseDashboard || (isSameDay(startDate, startOfToday()) || isBefore(startDate, startOfToday()));
        }
    }, [startDate, viewMode, canUseDashboard]);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Box
                className="no-print"
                display="flex"
                justifyContent="left"
                alignItems="center"
                gap={2}
                flexWrap="wrap"
                flexDirection={{ xs: "column-reverse", sm: "row" }}
            >
                <CalendarNavigator
                    navigateToNext={navigateToNext}
                    navigateToPrev={navigateToPrev}
                    disablePrevButton={disablePreviousWeekButton}
                    disableNextButton={disableNextWeekButton}
                    viewMode={viewMode}
                />
                <Box display="flex" alignItems="center" gap={1} flex={1}>
                    <MonthSelect
                        disabled={!canUseDashboard}
                        selectedMonth={selectedMonth}
                        availableMonths={availableMonths}
                        earliestPossibleDate={earliestPossibleDate}
                        setStartDate={(date) => {
                            switch (viewMode) {
                                case VIEW_MODE.WEEKLY:
                                    setStartDate(startOfWeek(date));
                                    setEndDate(endOfWeek(date));
                                    break;
                                case VIEW_MODE.DAILY:
                                default:
                                    setStartDate(date);
                                    setEndDate(date);
                                    break;
                            }
                        }}
                    />
                    <Button
                        variant="outlined"
                        onClick={() => {
                            switch (viewMode) {
                                case VIEW_MODE.WEEKLY:
                                    setStartDate(startOfWeek(startOfToday()));
                                    setEndDate(endOfWeek(startOfToday()));
                                    break;
                                case VIEW_MODE.DAILY:
                                default:
                                    setStartDate(startOfToday());
                                    setEndDate(startOfToday());
                                    break;
                            }
                        }}
                        sx={{
                            minWidth: 0,
                            ml: 1
                        }}
                        color="inherit"
                        startIcon={<TodayIcon />}
                        disabled={disableTodayButton}
                    >
                        Today
                    </Button>
                </Box>
                <Box display="flex" gap={1} justifyContent="flex-end" flexWrap="wrap">
                    {canUseDashboard && createLocation && (
                        <CreateLocationButton
                            createLocation={createLocation}
                            icon={<AddLocationAltIcon />}
                            buttonProps={{
                                color: "inherit",
                                variant: "outlined",
                                sx: { textTransform: "none", alignSelf: "flex-end" }
                            }}
                            text={isScreenSmallerThanSm ? "Location" : "Add Location"}
                        />
                    )}
                    {canUseDashboard && submitIssue &&
                        <CreateIssueButton
                            icon={<AddIcon />}
                            buttonProps={{
                                color: "inherit",
                                variant: "outlined",
                                sx: { textTransform: "none", alignSelf: "flex-end" }
                            }}
                            text={isScreenSmallerThanSm ? "Issue" : "Add Issue"}
                        />
                    }
                    <Box ml="auto">
                        <ViewSwitcher disabled={!canUseDashboard} />
                    </Box>
                </Box>
            </Box>
            <TableContainer sx={{ overflowX: { xs: "auto", md: 'unset' }, overflowY: "visible", maxHeight: { xs: "70dvh", md: "none" } }}>
                <Table stickyHeader size="small" sx={{ tableLayout: 'fixed' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                align="left"
                                sx={{
                                    borderStyle: "solid",
                                    borderWidth: "0px 0px 1px 0px",
                                    borderColor: "#C9C9C9",
                                    bgcolor: "#F9F9F9",
                                    color: theme => theme.palette.text.primary,
                                    fontWeight: 500,
                                    pb: 0.25,
                                    px: 0.5,
                                    width: { xs: LOCATION_COLUMN_WIDTH_ON_MOBILE, md: LOCATION_COLUMN_WIDTH_ON_DESKTOP },
                                    position: "sticky",
                                    left: 0,
                                    top: 0,
                                    zIndex: 3
                                }}
                            >
                                <TableSortLabel
                                    active
                                    direction={locationOrderBy}
                                    onClick={() => setLocationOrderBy(prevDirection => prevDirection === "asc" ? "desc" : "asc")}
                                >
                                    Location
                                </TableSortLabel>
                            </TableCell>
                            {dateColumnsToDisplay.map((dateString) => {
                                const date = parseISO(dateString);
                                const lastColumnDate = parseISO(dateColumnsToDisplay[dateColumnsToDisplay.length - 1]);
                                const inCurrentSelectedMonth = isSameMonth(date, lastColumnDate);
                                const isDateToday = isToday(date);
                                return (
                                    <TableCell
                                        key={dateString.toString()}
                                        align="center"
                                        sx={{
                                            borderStyle: "solid",
                                            borderWidth: "0px 0px 1px 0px",
                                            borderColor: "#C9C9C9",
                                            bgcolor: "#F9F9F9",
                                            fontWeight: 500,
                                            pb: 0.25,
                                            px: 0.75,
                                            color: theme => inCurrentSelectedMonth ? theme.palette.text.primary : grey[500],
                                            width: viewMode === VIEW_MODE.WEEKLY ? DATE_COLUMN_WIDTH_ON_DESKTOP : "auto"
                                        }}
                                    >
                                        <Box display="flex" justifyContent="space-between">
                                            <Typography fontWeight={isDateToday ? 700 : 400} fontSize="0.875rem">
                                                {isScreenSmallerThanMd ? format(date, "EEE") : format(date, "EEEE")}
                                            </Typography>
                                            <Typography fontWeight={isDateToday ? 700 : 400} fontSize="0.875rem">
                                                {format(date, "dd")}
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody
                        sx={(theme) => (
                            {
                                background:
                                    sortedLocations?.length > 0
                                        ? theme.palette.background.paper
                                        : 'transparent',
                            })}
                    >
                        {!canUseDashboard && createLocation && (
                            <TableRow>
                                <TableCell
                                    colSpan={dateColumnsToDisplay.length + 1}
                                    sx={{
                                        bgcolor: "white",
                                        border: 1,
                                        borderColor: "divider",
                                        borderTop: "none"
                                    }}
                                >
                                    <EmptyLocationDashboardState createLocation={createLocation} />
                                </TableCell>
                            </TableRow>
                        )}
                        {sortedLocations?.map((location) => (
                            <TableRow key={location.id}>
                                <TableCell
                                    align="left"
                                    sx={{
                                        borderStyle: "solid",
                                        borderWidth: "0px 1px 1px 1px",
                                        borderColor: "#C9C9C9",
                                        width: { xs: LOCATION_COLUMN_WIDTH_ON_MOBILE, md: LOCATION_COLUMN_WIDTH_ON_DESKTOP },
                                        position: { xs: "sticky", md: "unset" },
                                        left: 0,
                                        zIndex: 1,
                                        background: theme => theme.palette.background.paper
                                    }}
                                >
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                        <Typography align="left" fontSize="0.75rem" noWrap whiteSpace="nowrap" textOverflow="ellipsis" width="100%">
                                            {location.name}
                                        </Typography>
                                        <LocationContextMenu
                                            location={location}
                                            editLocation={editLocation}
                                            deleteLocation={deleteLocation}
                                            viewLocation={() => {
                                                navigateToPage(Page.Issues, new URLSearchParams(), false);
                                                updateFilter({ locationIds: [location.id] });
                                            }}
                                        />
                                    </Box>
                                </TableCell>
                                {dateColumnsToDisplay?.map((dateString) => {
                                    const date = parseISO(dateString);
                                    const locationIssuesOnDate = locationIdToDateToIssuesMap.get(location.id)?.get(dateString) ?? [];
                                    const locationAssessmentsOnDate = assessments.filter(assessment =>
                                        isSameDay(parseISO(assessment.createdAt), date) &&
                                        assessment.resourceId === location.id &&
                                        assessment.resourceType === PermissionResourceType.LOCATION
                                    );
                                    const isAssessmentRequired = locationIdToScheduleCheckerMap.get(location.id)?.isDateInSchedule(date) ?? false;
                                    let assessmentStatus = AssessmentStatus.NOT_APPLICABLE;
                                    if (locationAssessmentsOnDate.length === 0) {
                                        if (isAssessmentRequired) {
                                            assessmentStatus = AssessmentStatus.MISSING;
                                        } else {
                                            assessmentStatus = AssessmentStatus.NOT_APPLICABLE;
                                        }
                                    } else {
                                        if (locationAssessmentsOnDate.some(assessment => assessment.issueIds && assessment.issueIds.length > 0)) {
                                            assessmentStatus = AssessmentStatus.FAILED;
                                        } else {
                                            assessmentStatus = AssessmentStatus.PASSED;
                                        }
                                    }
                                    const resolved = locationIssuesOnDate.filter(issue => issue.status === IssueStatus.COMPLETED || issue.status === IssueStatus.ARCHIVED).length;
                                    const unresolved = locationIssuesOnDate.filter(issue => issue.status !== IssueStatus.COMPLETED && issue.status !== IssueStatus.ARCHIVED).length;
                                    return (
                                        <TableCell
                                            key={dateString}
                                            align="center"
                                            sx={{
                                                borderStyle: "solid",
                                                borderWidth: "0px 1px 1px 0px",
                                                borderColor: "#C9C9C9",
                                                fontSize: "0.75rem",
                                                width: viewMode === VIEW_MODE.WEEKLY ? DATE_COLUMN_WIDTH_ON_DESKTOP : "auto"
                                            }}
                                        >
                                            <IssueCountTableCellContent
                                                resolved={resolved}
                                                unresolved={unresolved}
                                                locationId={location.id}
                                                date={date}
                                                assessmentStatus={assessmentStatus}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                <Box sx={{ position: "sticky", padding: 1, bottom: 0, mt: 1, zIndex: 2, bgcolor: "white", border: 1, borderColor: "divider" }}>
                    <Legends />
                </Box>
            </TableContainer>
        </Box >
    );
};